import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Alert, Autocomplete, Box, Button, LinearProgress, Portal, TextField, Typography, TableCell, TableRow, TableBody, Table, TableContainer, Paper, InputLabel, Select, FormControl, MenuItem, SelectChangeEvent, Tooltip } from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { round } from 'lodash';
import { initPartner, PartnerType } from '../models/PartnersModel';
// import { PieChart } from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import { LightBlueBtnColor, HoverUnselectedLightBlueBtnColor, WhiteColor, brandColors, neutralColors } from '../helper/theme';
import { Months } from '../helper/utils';
import { Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isClientHomeRole } from '../helper/userRoles';

const PIE_CHART_COLORS = [
    brandColors.royalBlue,
    brandColors.navyBlue,
    brandColors.blue100,
    brandColors.orangeCustom,
    brandColors.purpleCustom,
    brandColors.green,
    brandColors.red,
    '#5c6bc0',
    '#26a69a',
];

const getMonthName = (month: number) => { return Months[month as keyof typeof Months]; };

const ERROR_TYPE_GRID_MIN_ROW_HEIGHT = 36;
const ERROR_TYPE_GRID_HEADER_HEIGHT = 56;

/** Set REACT_APP_MONTHLY_REPORT_TRANSACTIONS_UPPER_LIMIT (e.g. 100) to show a warning when exceeded; 0 or unset = off. */
const MONTHLY_TRANSACTIONS_UPPER_LIMIT = (() => {
    const parsed = parseInt(String(process.env.REACT_APP_MONTHLY_REPORT_TRANSACTIONS_UPPER_LIMIT ?? ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
})();

export interface MonthlyAccountPerformanceReportModel {
    month: number;
    year: number;
    partnersId: number;
};

/* Key metrics:
1. Total Transactions Reported — transactions received on open tax reports during the month
2. Filings Processed without Return — filings in Ready to File or Filed without return
3. Errors Found — total errors identified this month (matches error breakdown chart total)
4. Error Breakdown by Type and Frequency
*/

export interface MonthlyAccountPerformanceReportData {
    // 1. Total Transactions Reported to MISC during month
    totalTransactionsReported: number;
    // 2. # of Filings Processed without Return
    // totalFilingsProcessed: number;
    totalFilingsProcessed: {
        policyStatus: string;
        totalFilingsProcessed: number;
    }[];
    // 3. # of Filings Returned with Errors
    totalFilingsReturnedWithErrors: number;
    // 4. Error Breakdown by Type and Frequency
    errorBreakdownByTypeAndFrequency: ErrorBreakdownModel[];
    // 5. Filings by month
    filingsByMonth: FilingsByMonthModel[];
    // error breakdown by state and policy type
    errorBreakdownByStateAndPolicyType: ErrorBreakdownByStateAndPolicyTypeModel[];
};
export interface FilingsByMonthModel {
    month: number;
    year: number;
    totalTransactionsReported: number;
    filingsProcessedWithoutReturn: number;
    errorsFound: number;
};
export interface ErrorBreakdownModel {
    errorTagCodeId: number;
    errorTagName: string;
    errorsReported: number;
    percentageOfTotalReports: number;
};

//#region error breakdown
export interface ErrorBreakdownByStateAndPolicyTypeModel{
    policyNumber: string;
    stateId: number;
    stateAbbv: string;
    transactionTypeId: number;
    transactionType: string;  // transaction type name
    errorTagCodeId: number;
    errorTagName: string;
    errorsReported: number;
    percentageOfStatePolicyTotal: number;
};
export type FilterableField = 'state' | 'transactionType';
export interface FilterConfig {
    field: FilterableField;
    value?: string | number; // Optional filter value
};
export interface ErrorTagBreakdown {
    errorTagName: string;
    errorsReported: number;
    percentage: number;
};
export interface GroupedErrorBreakdown {
    groupKey: string;
    groupLabel: string;
    policyCount: number;
    totalErrors: number;
    breakdown: ErrorTagBreakdown[];
    items: ErrorBreakdownByStateAndPolicyTypeModel[];
};
export interface ErrorBreakdownTableRow {
    id: string;
    groupLabel: string;
    policyCount: number;
    totalErrors: number;
    breakdown: {
        errorTagName: string;
        errorsReported: number;
        percentage: number;
    }[];
    breakdownSummary: string;
};
//#endregion

export const MonthlyAccountPerformanceReport = () => {
    const { user } = useAuth();
    const token = user?.token;
    const RedColor = '#d32f2f';
    const IsClientUser = isClientHomeRole(user?.userRoles);
    const [loading, setLoading] = useState(false);
    const [partners, setPartners] = useState<PartnerType[]>([initPartner]);
    // const CurrentYear = new Date().getFullYear();
    const [formData, setFormData] = useState<MonthlyAccountPerformanceReportModel>({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        // 0 = "All Clients": MISC sees every client; a client user sees all of their own clients.
        partnersId: 0,
    });
    /** Month/year shown on metrics & charts — updated when Search/Clear completes, not when dropdowns change */
    const [reportedPeriod, setReportedPeriod] = useState<{ month: number; year: number }>({
        month: formData.month,
        year: formData.year,
    });
    const [performanceReportData, setPerformanceReportData] = useState<MonthlyAccountPerformanceReportData>({
        totalTransactionsReported: 0,
        totalFilingsProcessed: [],
        totalFilingsReturnedWithErrors: 0,
        errorBreakdownByTypeAndFrequency: [],
        filingsByMonth: [],
        errorBreakdownByStateAndPolicyType: [],
    });

    //#region API Calling
    const getPartners = () => {
        setLoading(true);
        apiClient(`/Partners/getPartners`, {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then(response => {
            if (response.status) {
                setPartners(response.data);
            }
        }).finally(() => {
            setLoading(false);
        });
    };
    /** Client portal users: load only the clients this user is mapped to (same source as the Filing Queue). */
    const getClientPartners = () => {
        setLoading(true);
        apiClient(`/customer/getPartnersList`, {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then(response => {
            if (response.status && Array.isArray(response.data)) {
                setPartners(response.data);
            }
        }).finally(() => {
            setLoading(false);
        });
    };
    const getMonthlyAccountPerformanceReport = (overrides?: Partial<MonthlyAccountPerformanceReportModel>) => {
        const params: MonthlyAccountPerformanceReportModel = { ...formData, ...overrides };
        setLoading(true);
        apiClient(`/getMonthlyAccountPerformanceReport?month=${params.month}&year=${params.year}&partnersId=${params.partnersId}`, {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then(response => {
            setPerformanceReportData(response.data);
            setReportedPeriod({ month: params.month, year: params.year });
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        if (IsClientUser) {
            getClientPartners();
        } else {
            getPartners();
        }
        getMonthlyAccountPerformanceReport();
    // }, [formData]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //#endregion API Calling

    const handleChange = (e: SelectChangeEvent<number>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const borderColor = '#e2e8f0';
    const selectSx = {
        '& .MuiOutlinedInput-notchedOutline': { borderColor },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: LightBlueBtnColor },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: LightBlueBtnColor + ' !important', borderWidth: '1px' },
    };

    const MonthDropdown = () => (
        <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select value={formData.month} name="month" 
                labelId="month-select-label" 
                label="Month"
                onChange={handleChange}
                sx={{ height: '2.75rem', ...selectSx }}
            >
                <MenuItem value={0}>Please select Month </MenuItem>
                {Object.keys(Months).map((month) => (
                    <MenuItem key={month} value={parseInt(month)}>{Months[parseInt(month) as keyof typeof Months]}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
    const YearDropdown = () => (
        <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select value={formData.year} name="year"
                labelId="year-select-label"
                onChange={handleChange}
                label="Year"
                sx={{ height: '2.75rem', ...selectSx }}
            >
                {/* past 10 years */}
                {Array.from({ length: 10 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
    const clientOptions: { partnersId: number; customerName: string }[] = [
        { partnersId: 0, customerName: 'All Clients' },
        ...partners.filter((p) => p.partnersId !== 0).map((p) => ({ partnersId: p.partnersId, customerName: p.customerName ?? '' })),
    ];
    /** Client portal users pick from their own mapped clients; "All Clients" = all of them combined. */
    const clientUserOptions: { partnersId: number; customerName: string }[] = [
        { partnersId: 0, customerName: 'All Clients' },
        ...partners.filter((p) => p.partnersId !== 0).map((p) => ({ partnersId: p.partnersId, customerName: p.customerName ?? '' })),
    ];
    /** Only show the client selector to a portal user when they are mapped to more than one client*/
    const hasMultipleClients = partners.filter((p) => p.partnersId !== 0).length > 1;
    const PartnerDropdown = () => (
        <Autocomplete
            options={clientOptions}
            getOptionLabel={(option) => option.customerName}
            value={clientOptions.find((opt) => opt.partnersId === formData.partnersId) ?? null}
            onChange={(_e, newValue) => setFormData({ ...formData, partnersId: newValue?.partnersId ?? 0 })}
            isOptionEqualToValue={(option, value) => option.partnersId === value.partnersId}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Client"
                    size="small"
                    sx={{
                        minWidth: 280,
                        '& .MuiOutlinedInput-root': { height: '2.75rem' },
                        ...selectSx,
                    }}
                />
            )}
        />
    );

    /** Flat modern styling aligned with site design */
    const pageHeaderBg = '#fafbfc';
    const tableHeaderBg = '#fafbfc';
    const tableRowAltBg = '#f8fafc';
    const textPrimary = '#0f172a';
    const textMuted = '#64748b';
    const borderColorFlat = '#e2e8f0';

    const BoxTitleStyle = {
        fontWeight: 600,
        fontSize: '0.9375rem',
        color: textPrimary,
        width: '100%',
        padding: '12px 16px',
        borderBottom: `1px solid ${borderColorFlat}`,
        backgroundColor: WhiteColor,
    };
    const TableCellDescriptionStyle = {
        fontWeight: 500,
        backgroundColor: tableHeaderBg,
        color: textPrimary,
        width: '400px',
        borderBottom: `1px solid ${borderColorFlat}`,
    };
    const TableCellValueStyle = {
        fontWeight: 500,
        backgroundColor: WhiteColor,
        color: textPrimary,
        borderBottom: `1px solid ${borderColorFlat}`,
    };
    const TableContainerStyle = (maxWidth: number | null = null) => ({
        ...(maxWidth && { maxWidth: maxWidth }),
        display: 'inline-block',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${borderColorFlat}`,
        boxShadow: 'none',
    });
    /** Side-by-side Error Type + Trending panels: equal width/height cards */
    const equalHeightPanelPaperSx = {
        ...TableContainerStyle(),
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        minWidth: 0,
        minHeight: 0,
        border: `1px solid ${borderColorFlat}`,
    };
    const TableStyle = {
        borderCollapse: 'collapse',
        '& td': {
            borderBottom: `1px solid ${borderColorFlat}`,
            padding: '10px 14px',
            textAlignLast: 'start',
        },
    };

    const getErrorTypeColor = useCallback((errorTagName: string) => {
        const index = performanceReportData.errorBreakdownByTypeAndFrequency.findIndex(
            (error) => error.errorTagName === errorTagName
        );
        if (index === -1) {
            return PIE_CHART_COLORS[0];
        }
        return PIE_CHART_COLORS[index % PIE_CHART_COLORS.length];
    }, [performanceReportData.errorBreakdownByTypeAndFrequency]);
    const getPieChartData = () => {
        return performanceReportData.errorBreakdownByTypeAndFrequency.filter(error => error.errorsReported > 0).map((error) => {
            return {
                id: error.errorTagCodeId,
                value: error.errorsReported,
                label: `${error.errorTagCodeId}. ${error.errorTagName}`,
                color: getErrorTypeColor(error.errorTagName),
            };
        });
    };
    const getChartData = () => {
        return Object.keys(Months).map((month) => {
            const monthNumber = parseInt(month);
            const filing = performanceReportData.filingsByMonth
                .filter(f => f.year === reportedPeriod.year)
                .find(f => f.month === monthNumber);

            return {
                month: Months[monthNumber as keyof typeof Months].slice(0, 3).toUpperCase(),
                totalTransactionsReported: filing?.totalTransactionsReported || 0,
                filingsProcessedWithoutReturn: filing?.filingsProcessedWithoutReturn || 0,
                errorsFound: filing?.errorsFound || 0,
            };
        });
    };
    const chartData = getChartData();
    const exceedsMonthlyTransactionUpperLimit = MONTHLY_TRANSACTIONS_UPPER_LIMIT > 0
        && performanceReportData.totalTransactionsReported > MONTHLY_TRANSACTIONS_UPPER_LIMIT;
    const trendingMonthsExceedingUpperLimit = MONTHLY_TRANSACTIONS_UPPER_LIMIT > 0
        ? chartData
            .filter((row) => row.totalTransactionsReported > MONTHLY_TRANSACTIONS_UPPER_LIMIT)
            .map((row) => row.month)
        : [];
    const columnOptions = {
        sortable: false,
        filterable: false,
    };
    const errorTypeColumns: GridColDef[] = [
        { field: 'errorType', headerName: 'Error Type',
            flex: 1,
            minWidth: 260,
            ...columnOptions,
            renderCell: (params: GridRenderCellParams) => {
                if(!params.row.errorTagName){return params.value;}
                return (
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Box sx={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '2px',
                            backgroundColor: getErrorTypeColor(params.row.errorTagName),
                            flexShrink: 0,
                        }} />
                        <Box sx={{ flex: 1 }}>{params.value}</Box>
                    </Box>
                );
            },
        },
        { field: 'errorsReported', headerName: 'Errors Reported',
            type: 'number',
            width: 160,
            align: 'center',
            headerAlign: 'center',
            ...columnOptions,
        },
        { field: 'percentageOfTotalReports', headerName: '% of Total',
            type: 'number',
            width: 140,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: (params) => `${params.value}%`,
            ...columnOptions,
        },
    ];
    const hasErrorTypeData = performanceReportData.errorBreakdownByTypeAndFrequency.some(error => error.errorsReported > 0);
    const errorTypeRows = performanceReportData.errorBreakdownByTypeAndFrequency.map((error, index) => {
        const errorTypeId = error.errorTagCodeId ?? index + 1;
        return {
            id: errorTypeId,
            errorType: `${errorTypeId}. ${error.errorTagName}`,
            errorTagName: error.errorTagName,
            errorsReported: error.errorsReported,
            percentageOfTotalReports: error.percentageOfTotalReports,
        };
    });
    const totalErrorsReported = performanceReportData.errorBreakdownByTypeAndFrequency.reduce(
        (acc, error) => acc + error.errorsReported, 0
    );
    const ErrorTypeNoRowsOverlay = () => (
        <Box sx={{ padding: '12px 16px', color: textMuted }}>
            No Entries
        </Box>
    );
    const errorTypeRowsWithTotal = hasErrorTypeData ? [
        ...errorTypeRows,
        {
            id: 'total',
            errorType: 'Total',
            errorsReported: totalErrorsReported,
            percentageOfTotalReports: 100,
        },
    ] : [];
    const errorTypeGridContainerRef = useRef<HTMLDivElement>(null);
    const [errorTypeRowHeight, setErrorTypeRowHeight] = useState(ERROR_TYPE_GRID_MIN_ROW_HEIGHT);
    useLayoutEffect(() => {
        const element = errorTypeGridContainerRef.current;
        const rowCount = errorTypeRowsWithTotal.length;
        if (!element || rowCount === 0) {
            return undefined;
        }
        const updateRowHeight = () => {
            const height = Math.floor(element.getBoundingClientRect().height);
            if (height <= 0) {
                return;
            }
            const bodyHeight = height - ERROR_TYPE_GRID_HEADER_HEIGHT;
            setErrorTypeRowHeight(Math.max(
                ERROR_TYPE_GRID_MIN_ROW_HEIGHT,
                Math.floor(bodyHeight / rowCount),
            ));
        };
        updateRowHeight();
        const resizeObserver = new ResizeObserver(updateRowHeight);
        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
    }, [errorTypeRowsWithTotal.length]);
    const selectedMonthField = `m${reportedPeriod.month}`;
    const filingsByMonthColumns: GridColDef[] = [
        {
            field: 'metric',
            headerName: `${reportedPeriod.year}`,
            minWidth: 135,
            ...columnOptions,
        },
        ...Object.keys(Months).map((month) => {
            const monthNumber = parseInt(month);
            const monthLabel = Months[monthNumber as keyof typeof Months].slice(0, 3).toUpperCase();
            return {
                field: `m${monthNumber}`,
                headerName: monthLabel,
                minWidth: 50,
                maxWidth: 80,
                flex: 1,
                align: 'right' as const,
                headerAlign: 'right' as const,
                ...columnOptions,
            };
        }),
    ];
    const filingsByMonthRows = (() => {
        const totalTransactionsRow: Record<string, number | string> = {
            id: 'totalTransactionsReported',
            metric: 'Total Transactions Reported',
        };
        const filingsProcessedRow: Record<string, number | string> = {
            id: 'filingsProcessedWithoutReturn',
            metric: 'Filings Processed Without Return',
        };
        const errorsFoundRow: Record<string, number | string> = {
            id: 'errorsFound',
            metric: 'Errors Found',
        };

        Object.keys(Months).forEach((month) => {
            const monthNumber = parseInt(month);
            const filing = performanceReportData.filingsByMonth
                .filter(f => f.year === reportedPeriod.year)
                .find(f => f.month === monthNumber);
            const monthKey = `m${monthNumber}`;

            totalTransactionsRow[monthKey] = filing?.totalTransactionsReported || 0;
            filingsProcessedRow[monthKey] = filing?.filingsProcessedWithoutReturn || 0;
            errorsFoundRow[monthKey] = filing?.errorsFound || 0;
        });

        return [totalTransactionsRow, filingsProcessedRow, errorsFoundRow];
    })();

    //#region error breakdown functions
    const buildBreakdown = (items: ErrorBreakdownByStateAndPolicyTypeModel[]) => {
        const totals = items.reduce((acc, item) => {
            acc.totalErrors += item.errorsReported;
            acc.byError[item.errorTagName] = (acc.byError[item.errorTagName] || 0) + item.errorsReported;
            return acc;
        }, {
            totalErrors: 0,
            byError: {} as Record<string, number>,
        });
        
        const breakdown = Object.entries(totals.byError)
            .map(([errorTagName, errorsReported]) => ({
                errorTagName,
                errorsReported,
                percentage: totals.totalErrors ? round((errorsReported / totals.totalErrors) * 100, 0) : 0,
            }))
            .sort((a, b) => b.errorsReported - a.errorsReported);
        
        return { totalErrors: totals.totalErrors, breakdown };
    };
    
    const getFilteredErrorBreakdown = (data: ErrorBreakdownByStateAndPolicyTypeModel[], groupBy: FilterableField, filters?: FilterConfig[]): GroupedErrorBreakdown[] => {
        // Apply filters first
        let filteredData = data;
        
        if (filters && filters.length > 0) {
            filteredData = data.filter(item => {
                return filters.every(filter => {
                    switch (filter.field) {
                        case 'state':
                            return filter.value === undefined ||
                                item.stateAbbv === filter.value ||
                                item.stateId === filter.value;
                        case 'transactionType':
                            return filter.value === undefined ||
                                item.transactionType === filter.value ||
                                item.transactionTypeId === filter.value;
                        default:
                            return true;
                    };
                });
            });
        }
        
        // Group by the specified field
        const grouped = filteredData.reduce((acc, item) => {
            const groupedLabels = {
                state: {
                    key: `${item.stateId}-${item.stateAbbv}`,
                    label: item.stateAbbv,
                },
                transactionType: {
                    key: `${item.transactionTypeId}-${item.transactionType}`,
                    label: item.transactionType,
                },
                default: {
                    key: 'unknown',
                    label: 'Unknown',
                },
            };
            
            const { key, label } = groupedLabels[groupBy] || groupedLabels.default;
            
            if (!acc[key]) {
                acc[key] = {
                    groupKey: key,
                    groupLabel: label,
                    policyNumbers: new Set<string>(),
                    items: [] as ErrorBreakdownByStateAndPolicyTypeModel[],
                };
            }
            
            acc[key].policyNumbers.add(item.policyNumber);
            acc[key].items.push(item);
            return acc;
        }, {} as Record<string, {
            groupKey: string;
            groupLabel: string;
            policyNumbers: Set<string>;
            items: ErrorBreakdownByStateAndPolicyTypeModel[];
        }>);

        // Build breakdown for each group
        return Object.values(grouped).map(group => {
            const { totalErrors, breakdown } = buildBreakdown(group.items);
            return {
                groupKey: group.groupKey,
                groupLabel: group.groupLabel,
                policyCount: group.policyNumbers.size,
                totalErrors,
                breakdown,
                items: group.items,
            };
        }).sort((a, b) => b.totalErrors - a.totalErrors);
    };
    const convertToTableRows = (groups: GroupedErrorBreakdown[], sortBy: 'label' | 'errors' = 'label'): ErrorBreakdownTableRow[] => {
        const rows = groups.map((group, groupIndex) => ({
            id: `${group.groupLabel}-${groupIndex}`,
            groupLabel: group.groupLabel,
            policyCount: group.policyCount,
            totalErrors: group.totalErrors,
            breakdown: group.breakdown,
            breakdownSummary: `${group.breakdown.length} error type${group.breakdown.length === 1 ? '' : 's'}`,
        }));
        
        // Sort based on preference
        if (sortBy === 'label') {
            return rows.sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
        } else {
            return rows.sort((a, b) => b.totalErrors - a.totalErrors);
        };
    };
    const renderBreakdownTooltipContent = (breakdown: ErrorTagBreakdown[]) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '260px' }}>
            {breakdown.map((item) => (
                <Box key={item.errorTagName} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Box sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        backgroundColor: getErrorTypeColor(item.errorTagName),
                        flexShrink: 0,
                    }} />
                    <Box sx={{ flex: 1 }}>{item.errorTagName}</Box>
                    <Box sx={{ minWidth: '70px', textAlign: 'right' }}>{item.errorsReported}</Box>
                </Box>
            ))}
        </Box>
    );
    const getErrorBreakdownColumns = (headerName: string, width: number): GridColDef[] => {
        const columns: GridColDef[] = [
            { field: 'groupLabel', headerName: headerName, width: width, ...columnOptions },
            { field: 'policyCount', headerName: 'Transactions',
                type: 'number',
                width: 140,
                align: 'right', headerAlign: 'right',
                ...columnOptions,
            },
            { field: 'totalErrors', headerName: 'Total Errors',
                type: 'number',
                width: 140,
                align: 'right', headerAlign: 'right',
                ...columnOptions,
            },
            { field: 'breakdownSummary', headerName: 'Error Breakdown',
                flex: 1, minWidth: 260,
                /*
                renderCell: (params) => (
                    <Tooltip arrow
                        title={renderBreakdownTooltipContent(params.row.breakdown)}
                        slotProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    padding: '8px 10px',
                                },
                            },
                            arrow: { sx: { color: '#fff' } },
                        }}
                    >
                        <span>{params.value}</span>
                    </Tooltip>
                ),
                */
                ...columnOptions,
            },
        ];
        return columns;
    };

    const errorBreakdownByStateTableGroups = getFilteredErrorBreakdown(
        performanceReportData.errorBreakdownByStateAndPolicyType,
        'state'
    );
    const hasErrorBreakdownByStateTableData = errorBreakdownByStateTableGroups.length > 0;
    const errorBreakdownByStateTableRows = convertToTableRows(errorBreakdownByStateTableGroups, 'label');
    const errorBreakdownByStateTableColumns = getErrorBreakdownColumns('State', 120);


    const errorBreakdownByTransactionTypeTableGroups = getFilteredErrorBreakdown(
        performanceReportData.errorBreakdownByStateAndPolicyType,
        'transactionType'
    );
    const hasErrorBreakdownByTransactionTypeTableData = errorBreakdownByTransactionTypeTableGroups.length > 0;
    const errorBreakdownByTransactionTypeTableRows = convertToTableRows(errorBreakdownByTransactionTypeTableGroups, 'label');
    const errorBreakdownByTransactionTypeTableColumns = getErrorBreakdownColumns('Transaction Type', 200);

    const getDetailPanelHeight = useCallback((params: GridRowParams) => Math.max(52, params.row.breakdown.length * 28 + 16), []);

    const dataGridHeaderSx = {
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: tableHeaderBg,
            color: textPrimary,
            fontWeight: 600,
            borderBottom: `1px solid ${borderColorFlat}`,
        },
        '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${borderColorFlat}`,
        },
    };
    const errorBreakdownDetailPanelContent = useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => (
        <Box sx={{ padding: '8px 16px' }}>
            {row.breakdown.map((item: { errorTagName: string; percentage: number; errorsReported: number }) => (
                <Box key={item.errorTagName} sx={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '2px 0' }}>
                    <Box sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        backgroundColor: getErrorTypeColor(item.errorTagName),
                        flexShrink: 0,
                    }} />
                    <Box sx={{ flex: 1 }}>{item.errorTagName} - {item.errorsReported} ({item.percentage}%)</Box>
                </Box>
            ))}
        </Box>
    ), [getErrorTypeColor]);
    const errorBreakdownTableStyles = {
        border: 'none',
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: tableHeaderBg,
            color: textPrimary,
            fontWeight: 600,
            borderBottom: `1px solid ${borderColorFlat}`,
        },
        '& .MuiDataGrid-detailPanel': {
            backgroundColor: tableRowAltBg,
        },
        '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${borderColorFlat}`,
        },
    };
    //#endregion error breakdown functions
    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ width: '100%' }}>
            <Portal>
                <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
                    {loading && <LinearProgress color="info" sx={{ backgroundColor: neutralColors.darkBlueGray }} />}
                </div>
            </Portal>
            <Box sx={{ width: '100%' }} className="customer-header">
                {/* Page header - flat bar matching site */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    px: { xs: 4, md: 2 },
                    py: 1.5,
                    borderBottom: `1px solid ${borderColorFlat}`,
                    backgroundColor: pageHeaderBg,
                    minHeight: 56,
                    marginBottom: 2,
                    marginLeft: { xs: '-24px', md: '0px' },
                    marginRight: { xs: '-24px', md: '0px' },
                }}>
                    <Typography component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, color: textPrimary }}>
                        Monthly Account Performance Report
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <MonthDropdown />
                        <YearDropdown />
                        {IsClientUser ? (
                            hasMultipleClients && (
                            <Grid item xl={3} lg={4} sm={6} xs={12}>
                                <Autocomplete
                                    options={clientUserOptions}
                                    getOptionLabel={(option) => option.customerName}
                                    value={clientUserOptions.find((opt) => opt.partnersId === formData.partnersId) ?? clientUserOptions[0]}
                                    onChange={(_e, newValue) => setFormData({ ...formData, partnersId: newValue?.partnersId ?? 0 })}
                                    isOptionEqualToValue={(option, value) => option.partnersId === value.partnersId}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Client"
                                            size="small"
                                            sx={{
                                                minWidth: 280,
                                                '& .MuiOutlinedInput-root': { height: '2.75rem' },
                                                ...selectSx,
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            )
                        ) : (
                            <Grid item xl={3} lg={4} sm={6} xs={12}>
                                <PartnerDropdown />
                            </Grid>
                        )}
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={() => getMonthlyAccountPerformanceReport()}
                            startIcon={<Search />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                backgroundColor: LightBlueBtnColor,
                                '&:hover': { backgroundColor: '#176990' },
                                px: 2,
                            }}
                        >
                            Search
                        </Button>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => {
                                const initial = {
                                    month: new Date().getMonth() + 1,
                                    year: new Date().getFullYear(),
                                    partnersId: 0,
                                };
                                setFormData(initial);
                                getMonthlyAccountPerformanceReport(initial);
                            }}
                            startIcon={<Close />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: borderColor,
                                color: textPrimary,
                                '&:hover': {
                                    borderColor: LightBlueBtnColor,
                                    backgroundColor: HoverUnselectedLightBlueBtnColor,
                                    color: textPrimary,
                                },
                                px: 2,
                            }}
                        >
                            Clear
                        </Button>
                    </Box>
                </Box>
                
                
                <Box
                    sx={{
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        mb: 2,
                        px: 3,
                        py: 2.5,
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                        border: '1px solid #e2e8f0',
                    }}
                >
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#334155', mb: 0.25 }}>
                        Key metrics — {getMonthName(reportedPeriod.month)} {reportedPeriod.year}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: '#64748b', mb: 2 }}>
                        Summary for the selected period.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                        
                            <Box sx={{
                                px: 2,
                                py: 1,
                                borderRadius: '8px',
                                backgroundColor: exceedsMonthlyTransactionUpperLimit ? '#fffbeb' : '#f8fafc',
                                color: '#334155',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                border: exceedsMonthlyTransactionUpperLimit ? '1px solid #f59e0b' : 'none',
                            }}>
                                <Tooltip title="Total number of transactions received on open tax reports during a given month" arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#64748b' }} />
                                </Tooltip>
                                Total Transactions Reported: {performanceReportData.totalTransactionsReported}
                            </Box>
                            <Box sx={{ px: 2, py: 1, borderRadius: '8px', backgroundColor: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title="Total number of filings processed without return during a given month" arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#64748b', alignSelf: 'center' }} />
                                </Tooltip>
                                Filings Processed without Return: {performanceReportData.totalFilingsProcessed[0]?.totalFilingsProcessed ?? 0}
                            </Box>
                            <Box sx={{ px: 2, py: 1, borderRadius: '8px', backgroundColor: '#fef2f2', color: '#b91c1c', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title="Total number of errors identified this month" arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#b91c1c', alignSelf: 'center' }} />
                                </Tooltip>
                                Errors Found: {totalErrorsReported}
                            </Box>
                    </Box>
                    {exceedsMonthlyTransactionUpperLimit && (
                        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, fontSize: '0.8125rem' }}>
                            Total Transactions Reported ({performanceReportData.totalTransactionsReported}) exceeds the monthly upper limit of {MONTHLY_TRANSACTIONS_UPPER_LIMIT} for {getMonthName(reportedPeriod.month)} {reportedPeriod.year}. Counts are still shown in full; review volume with your account team if unexpected.
                        </Alert>
                    )}
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: '#64748b', mt: 1.5, mb: 0 }}>
                        Processed transactions may lag the transactions reported during the month due to pending client documentation.
                    </Typography>
                </Box>
                </Box>

                {/* main layout - equal columns, full width */}
                <Grid container spacing={2} sx={{ mt: 2, alignItems: 'stretch' }}>
                    {/* left column */}
                    <Grid item sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0%', minWidth: 0, minHeight: 0 }}>
                        {/* filings sorted by error type */}
                        <Paper elevation={0} sx={equalHeightPanelPaperSx}>
                            <Box sx={BoxTitleStyle}>Filings returned with errors by Error Type — {getMonthName(reportedPeriod.month)} {reportedPeriod.year}</Box>
                            <Box
                                ref={errorTypeGridContainerRef}
                                sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}
                            >
                                <DataGridPro
                                    rows={errorTypeRowsWithTotal}
                                    columns={errorTypeColumns}
                                    disableSelectionOnClick
                                    disableChildrenSorting
                                    hideFooter
                                    rowHeight={errorTypeRowHeight}
                                    style={{
                                        height: errorTypeRowsWithTotal.length > 0
                                            ? errorTypeRowHeight * errorTypeRowsWithTotal.length + ERROR_TYPE_GRID_HEADER_HEIGHT
                                            : '100%',
                                        minHeight: errorTypeRowsWithTotal.length > 0
                                            ? errorTypeRowHeight * errorTypeRowsWithTotal.length + ERROR_TYPE_GRID_HEADER_HEIGHT
                                            : 120,
                                    }}
                                    disableColumnMenu
                                    disableColumnReorder
                                    disableColumnFilter
                                    disableColumnSelector
                                    disableColumnPinning
                                    components={{ NoRowsOverlay: ErrorTypeNoRowsOverlay }}
                                    getRowClassName={(params) =>
                                        params.row?.id === 'total' ? 'error-type-total-row' : ''
                                    }
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        width: '100%',
                                        border: 'none',
                                        ...dataGridHeaderSx,
                                        '& .MuiDataGrid-cell': {
                                            ...dataGridHeaderSx['& .MuiDataGrid-cell'],
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                        '& .error-type-total-row .MuiDataGrid-cell': {
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* right column */}
                    <Grid item sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0%', minWidth: 0, minHeight: 0 }}>
                        {/* Trending Over the Year */}
                        <Paper elevation={0} sx={equalHeightPanelPaperSx}>
                            <Box sx={BoxTitleStyle}>Trending Over the Year — {reportedPeriod.year}</Box>
                            {trendingMonthsExceedingUpperLimit.length > 0 && (
                                <Alert severity="info" sx={{ mx: 2, mb: 1, py: 0.5, fontSize: '0.8125rem' }}>
                                    Months above the transaction upper limit ({MONTHLY_TRANSACTIONS_UPPER_LIMIT}): {trendingMonthsExceedingUpperLimit.join(', ')}.
                                </Alert>
                            )}
                            <DataGridPro rows={filingsByMonthRows} columns={filingsByMonthColumns}
                                autoHeight
                                disableSelectionOnClick
                                hideFooter
                                rowHeight={36}
                                disableColumnMenu
                                disableColumnReorder
                                disableColumnFilter
                                disableColumnSelector
                                disableColumnPinning
                                getCellClassName={(params) => {
                                    if (params.field === selectedMonthField && params.row?.metric) {
                                        return 'selected-month-metric-cell';
                                    }
                                    return '';
                                }}
                                sx={{
                                    border: 'none',
                                    ...dataGridHeaderSx,
                                    '& .selected-month-metric-cell': {
                                        fontWeight: 600,
                                    },
                                }}
                            />
                            {/* Pie chart moved into the empty space below the Trending table */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 2,
                                borderTop: `1px solid ${borderColorFlat}`,
                            }}>
                                <Typography component="h3" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: textPrimary, marginBottom: 1 }}>By Error Type</Typography>
                                {hasErrorTypeData ? (
                                    <PieChart width={600} height={400}
                                        series={[
                                            {
                                                data: getPieChartData(),
                                                arcLabel: (item) => item.id?.toString() || '0',
                                                innerRadius: 0,
                                                outerRadius: '95%',
                                                cornerRadius: 4,
                                                paddingAngle: 2,
                                            }
                                        ]}
                                        margin={{ left: 100 }}
                                        slotProps={{
                                            legend: { hidden: true },
                                        }}
                                        sx={{
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            '& .MuiChartsLegend-mark': { rx: 2 },
                                            '& .MuiChartsLegend-label': { fontSize: '0.8125rem' },
                                        }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: '100%',
                                        minHeight: 200,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: textMuted,
                                    }}>No Entries</Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Error Breakdown by State / Transaction Type */}
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* left column - Error Breakdown by State */}
                    <Grid item xs={12} lg={6} >
                        <Paper elevation={0} sx={{ ...TableContainerStyle(), width: '100%', minWidth: 0, border: `1px solid ${borderColorFlat}` }}>
                            <Box sx={BoxTitleStyle}>Error Breakdown by State</Box>
                            {hasErrorBreakdownByStateTableData ? (
                                <DataGridPro rows={errorBreakdownByStateTableRows} 
                                    columns={errorBreakdownByStateTableColumns} 
                                    autoHeight
                                    disableSelectionOnClick
                                    hideFooter
                                    rowHeight={32}
                                    disableColumnMenu
                                    disableColumnReorder
                                    disableColumnFilter
                                    disableColumnSelector
                                    disableColumnPinning
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={errorBreakdownDetailPanelContent}
                                    sx={errorBreakdownTableStyles}
                                />
                            ) : (
                                <Box sx={{
                                    width: '100%',
                                    padding: '16px 12px',
                                    color: textMuted,
                                }}>No Entries</Box>
                            )}
                        </Paper>
                    </Grid>
                    {/* right column - Error Breakdown by Transaction Type */}
                    <Grid item xs={12} lg={6} >
                        <Paper elevation={0} sx={{ ...TableContainerStyle(), width: '100%', minWidth: 0, border: `1px solid ${borderColorFlat}` }}>
                            <Box sx={BoxTitleStyle}>Error Breakdown by Transaction Type</Box>
                            {hasErrorBreakdownByTransactionTypeTableData ? (
                                <DataGridPro rows={errorBreakdownByTransactionTypeTableRows} 
                                    columns={errorBreakdownByTransactionTypeTableColumns} 
                                    autoHeight
                                    disableSelectionOnClick
                                    hideFooter
                                    rowHeight={32}
                                    disableColumnMenu
                                    disableColumnReorder
                                    disableColumnFilter
                                    disableColumnSelector
                                    disableColumnPinning
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={errorBreakdownDetailPanelContent}
                                    sx={errorBreakdownTableStyles}
                                />
                            ) : (
                                <Box sx={{
                                    width: '100%',
                                    padding: '16px 12px',
                                    color: textMuted,
                                }}>No Entries</Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

            </Box>
        </LocalizationProvider>
    );
};